import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate } from '../api/dataverseClient';
import { getAccount } from '../auth/authService';
import { setResolvedEnvironmentUrl } from './environmentService';

// ── Runtime (per-tenant) license path ──────────────────────────────────
//
// New, ADDITIVE flow that sits alongside the existing build-time key path.
// A tenant that has stored a raw key in the `dagrunpm_LicenseKey` Dataverse
// environment variable gets a fresh JWT by redeeming that key against the
// platform. A tenant with NO stored value (including Scott's own live tenant)
// reads empty here and the caller falls back to the unchanged build-time
// REACT_APP_LICENSE_KEY + GET /status path. Any failure in this module also
// resolves to "no runtime key" so the fallback still protects existing tenants.

const ENV_VAR_SCHEMA = 'dagrunpm_LicenseKey';
const JWT_SESSION_KEY = 'dagrun_pm_license_jwt';

export type LicenseMode = 'runtime' | 'buildtime';
export interface LicenseCheckResult {
  ok: boolean;
  mode: LicenseMode;
  status?: string;
  expiresAt?: string;
}

interface EnvVarValue {
  environmentvariablevalueid: string;
  value: string | null;
}
interface EnvVarDef {
  environmentvariabledefinitionid: string;
  defaultvalue: string | null;
  environmentvariabledefinition_environmentvariablevalue?: EnvVarValue[];
}

async function loadEnvVarDefinition(pca: IPublicClientApplication): Promise<EnvVarDef | null> {
  const defs = await dvList<EnvVarDef>(pca, 'environmentvariabledefinitions', {
    select: ['environmentvariabledefinitionid', 'defaultvalue'],
    filter: `schemaname eq '${ENV_VAR_SCHEMA}'`,
    expand:
      'environmentvariabledefinition_environmentvariablevalue($select=value,environmentvariablevalueid)',
  });
  return defs[0] ?? null;
}

/**
 * The raw license key stored in this tenant's Dataverse env variable, or null
 * when the definition/value is absent or empty. Never throws — any error means
 * "no runtime key" so the caller uses the build-time fallback.
 */
export async function readStoredLicenseKey(pca: IPublicClientApplication): Promise<string | null> {
  try {
    const def = await loadEnvVarDefinition(pca);
    if (!def) return null;
    const valueRow = def.environmentvariabledefinition_environmentvariablevalue?.[0];
    const raw = (valueRow?.value ?? def.defaultvalue ?? '').trim();
    return raw.length ? raw : null;
  } catch {
    return null;
  }
}

/**
 * Upsert the raw key into the tenant's `dagrunpm_LicenseKey` env variable.
 * Requires the definition to exist (it ships in the DagrunPM solution).
 */
export async function storeLicenseKey(pca: IPublicClientApplication, key: string): Promise<void> {
  const def = await loadEnvVarDefinition(pca);
  if (!def) {
    throw new Error(
      `The ${ENV_VAR_SCHEMA} environment variable is not present in this environment. Import the latest Dagrun PM solution and try again.`,
    );
  }
  const existing = def.environmentvariabledefinition_environmentvariablevalue?.[0];
  if (existing) {
    await dvUpdate(pca, 'environmentvariablevalues', existing.environmentvariablevalueid, { value: key });
  } else {
    await dvCreate(pca, 'environmentvariablevalues', {
      value: key,
      'EnvironmentVariableDefinitionId@odata.bind': `/environmentvariabledefinitions(${def.environmentvariabledefinitionid})`,
    });
  }
}

/**
 * Redeem a raw key against the platform for a signed JWT (bound to this
 * tenant). Caches the JWT in sessionStorage. Returns ok=false on any failure.
 */
export async function redeemKey(
  pca: IPublicClientApplication,
  key: string,
  dataverseEnvironmentUrl?: string,
): Promise<LicenseCheckResult> {
  const platformUrl = process.env.REACT_APP_PLATFORM_URL;
  if (!platformUrl) return { ok: false, mode: 'runtime' };
  const account = await getAccount(pca);
  const tenantId = account?.tenantId;
  if (!tenantId) return { ok: false, mode: 'runtime' };
  try {
    const requestBody: Record<string, unknown> = { licenseKey: key, m365TenantId: tenantId };
    // Only include when the Setup Wizard resolved one — omitting it leaves the
    // stored value untouched (Scott's tenant never sends one).
    if (dataverseEnvironmentUrl && dataverseEnvironmentUrl.trim()) {
      requestBody.dataverseEnvironmentUrl = dataverseEnvironmentUrl.trim();
    }
    const res = await fetch(`${platformUrl}/api/license/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!res.ok) return { ok: false, mode: 'runtime' };
    const data = await res.json();
    const ok = data.allowed === true && data.status === 'active';
    if (ok && data.token) sessionStorage.setItem(JWT_SESSION_KEY, data.token as string);
    // Persist the tenant's resolved Dataverse environment URL returned by the
    // platform so getEnvironmentUrl() uses it on this and subsequent loads.
    // When null (no stored value — e.g. Scott's tenant) this is a no-op and the
    // build-time fallback stays in effect.
    if (ok && typeof data.dataverseEnvironmentUrl === 'string') {
      setResolvedEnvironmentUrl(data.dataverseEnvironmentUrl);
    }
    return { ok, mode: 'runtime', status: data.status, expiresAt: data.expiresAt };
  } catch {
    return { ok: false, mode: 'runtime' };
  }
}

/**
 * The existing build-time license check — logic UNCHANGED from the original
 * checkLicense. This is the fallback for any tenant without a stored key.
 */
async function checkBuildTimeLicense(): Promise<boolean> {
  const licenseKey = process.env.REACT_APP_LICENSE_KEY;
  const platformUrl = process.env.REACT_APP_PLATFORM_URL;
  if (!licenseKey || !platformUrl) return false;
  try {
    const response = await fetch(
      `${platformUrl}/api/license/status?key=${encodeURIComponent(licenseKey)}`,
    );
    if (!response.ok) return false;
    const result = await response.json();
    return result.status === 'active' && result.isExpired === false;
  } catch {
    return false;
  }
}

/**
 * Main license check. Runtime path when a key is stored in Dataverse; otherwise
 * the unchanged build-time fallback (which is what keeps existing tenants,
 * including Scott's own, working exactly as before).
 */
export async function verifyLicense(pca: IPublicClientApplication): Promise<LicenseCheckResult> {
  const storedKey = await readStoredLicenseKey(pca);
  if (storedKey) {
    return redeemKey(pca, storedKey);
  }
  const ok = await checkBuildTimeLicense();
  return { ok, mode: 'buildtime' };
}
